<template>
  <div class="r6-light">
    <CyberboardR6CdcPanel
      :baseData="profileNew.data"
      :defaultData="profileNew.default"
      :syncStatus="syncStatus || (isSyncing ? 'syncInProgress' : '')"
      @ready="onCdcReady"
      @state="onCdcState"
      @data="onCdcData"
      @error="onCdcError"
    />
    <CyberboardR6ConfigSelection
      v-model="showSyncSelection"
      purpose="axis-sync"
      :currentPagePosition="syncPagePosition"
      @confirm="syncAxisLights"
    />
    <InSwitchLEDNew
      ref="axisLightEditor"
      :keyboardPath="$paths.keyboardR6"
      :backPath="$paths.indexR6"
      :lightIndices="lightIndices"
      pcbLayout="r6"
      :showKeyboardLink="false"
    >
      <template slot="before-actions">
        <CyberboardR6SyncProgress
          v-if="isSyncing"
          :label="syncProgressLabel"
          :percent="syncProgressPercent"
        />
      </template>
      <template slot="actions">
        <button
          class="r6-sync-button"
          type="button"
          :disabled="isSyncing"
          @click="openAxisSyncSelection"
        >
          {{ $t("cyberboardR6.sync") }}
        </button>
      </template>
    </InSwitchLEDNew>
    <CyberboardR6Navigation />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { deepCopy } from "@/utils";
import InSwitchLEDNew from "@/views/CyberboardNew/InSwitchLEDNew.vue";
import cyberboardR6SyncMixin from "./utils/cyberboardR6SyncMixin";
import CyberboardR6CdcPanel from "./CyberboardR6CdcPanel.vue";
import CyberboardR6SyncProgress from "./CyberboardR6SyncProgress.vue";
import CyberboardR6ConfigSelection from "./CyberboardR6ConfigSelection.vue";
import CyberboardR6Navigation from "./CyberboardR6Navigation.vue";
import {
  buildR6SyncSelectionPlan,
  getR6SwitchLightPageIndices,
} from "./r6Profile";

export default {
  name: "InSwitchLEDR6",
  components: {
    CyberboardR6CdcPanel,
    CyberboardR6SyncProgress,
    CyberboardR6ConfigSelection,
    CyberboardR6Navigation,
    InSwitchLEDNew,
  },
  mixins: [cyberboardR6SyncMixin],
  data() {
    return { showSyncSelection: false, syncPagePosition: 0 };
  },
  computed: {
    ...mapState(["profileNew"]),
    lightIndices() {
      return getR6SwitchLightPageIndices();
    },
  },
  methods: {
    openAxisSyncSelection() {
      const lightIndex = Number(this.$refs.axisLightEditor?.lightIndex);
      this.syncPagePosition = this.lightIndices.indexOf(lightIndex);
      this.showSyncSelection = true;
    },
    async syncAxisLights(selection) {
      const editor = this.$refs.axisLightEditor;
      if (!editor || !Array.isArray(selection?.axis)) return false;

      const plan = buildR6SyncSelectionPlan(
        { axis: selection.axis },
        this.profileNew.data.page_data
      );
      const selectedPageIndices = Object.keys(plan.pageFeatureScope)
        .filter((pageIndex) =>
          plan.pageFeatureScope[pageIndex].includes("axisRgb")
        )
        .map(Number);
      if (!selectedPageIndices.length) return false;

      const currentPageIndex = Number(editor.lightIndex);
      const saveResult = editor.onSave(false);
      if (!saveResult) return false;
      await saveResult;
      await this.$nextTick();

      const data = deepCopy(this.profileNew.data);
      selectedPageIndices.forEach((pageIndex) => {
        const page = data.page_data.find(
          (item) => Number(item.page_index) === pageIndex
        );
        if (!page) return;
        if (page.keyframes) page.keyframes.valid = 1;
      });
      await this.$store.dispatch("profileNew/setData", data);
      await this.$nextTick();

      if (this.r6Bridge) {
        this.r6Bridge.log("axis-light-sync:prepared", {
          featureScope: plan.featureScope,
          pageFeatureScope: plan.pageFeatureScope,
          currentPageIndex,
          pages: selectedPageIndices.map((pageIndex) => {
            const page = data.page_data.find(
              (item) => Number(item.page_index) === pageIndex
            );
            return {
              pageIndex,
              pageValid: page?.valid,
              axisFrameCount: page?.keyframes?.frame_num || 0,
              sampleAxisRgb: page?.keyframes?.frame_data?.[0]?.frame_RGB?.slice(
                0,
                3
              ),
            };
          }),
        });
      }
      // Sync only the selected axis-light data; leave page enablement untouched.
      return this.syncToDevice(
        plan.featureScope,
        undefined,
        plan.pageFeatureScope
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.r6-light {
  min-height: inherit;

  .cdc-panel {
    color: #ffffff;
  }

  // R6 PCB uses a shorter natural image than the legacy fixed-height canvas.
  ::v-deep .light-panel .lights {
    height: auto;
  }

  ::v-deep .c-led-pcb.r6-layout .leds {
    // Preserve hitbox positions after the canvas switches to natural image height.
    top: 9.2%;
    height: 80.5%;
  }

  ::v-deep .c-led-pcb.r6-layout .spotlights {
    display: none;
  }

  .r6-sync-button {
    @include button;
    width: 180px;
    margin: 0;
    cursor: pointer;

    &:disabled {
      cursor: wait;
      opacity: 0.65;
    }
  }

  @media (max-width: 420px) {
    .r6-sync-button {
      margin-top: 4px;
    }
  }
}
</style>
